import { TABLET_WIDTH } from "helpers/ScreenSizes";
import { useWindowSize } from "hooks/useWindowSize/useWindowSize";
import { cloneDeep } from "lodash";
import { useMemo, useState } from "react";

export type TTeeSheetSideBySideView = "stacked" | "side-by-side";

export type TTeeSheetView = "default" | TTeeSheetSideBySideView;

export interface ITeeSheetSideBySideSettingsV1 {
  version: 1;
  enabled: boolean;
  view: TTeeSheetSideBySideView;
  selectedDivisionIds: number[];
  hideTurnTimes: boolean;
  hideBlockedTimes: boolean;
  syncDivisionScroll: boolean;
  disableAutoScroll: boolean;
  hideBookingCategories: boolean;
}

export type ITeeSheetSideBySideSettings = ITeeSheetSideBySideSettingsV1;

export type IUpdateTeeSheetSideBySideSettings = (
  sideBySideSettingsKeyPrefix: string,
  updatedSideBySideSettings: ITeeSheetSideBySideSettings,
) => void;

interface IUseTeeSheetSideBySideSettings {
  sideBySideSettings: ITeeSheetSideBySideSettings;
  teeSheetView: TTeeSheetView;
  loadSideBySideSettings: (sideBySideSettingsKeyPrefix: string) => void;
  updateSideBySideSettings: IUpdateTeeSheetSideBySideSettings;
}

const CURRENT_SIDE_BY_SIDE_SETTINGS_VERSION = 1;

const SIDE_BY_SIDE_SETTINGS_KEY_SUFFIX = "tee_sheet_side_by_side_settings";

export const defaultSideBySideSettings: ITeeSheetSideBySideSettings = {
  version: CURRENT_SIDE_BY_SIDE_SETTINGS_VERSION,
  enabled: false,
  view: "stacked",
  selectedDivisionIds: [],
  hideTurnTimes: false,
  hideBlockedTimes: false,
  syncDivisionScroll: false,
  disableAutoScroll: false,
  hideBookingCategories: false,
};

const isLocalStorageAvailable = localStorageSupportTest();

function localStorageSupportTest() {
  const test = "__test__";

  try {
    window.localStorage.setItem(test, test);
    window.localStorage.removeItem(test);
    return true;
  } catch {
    return false;
  }
}

type TLoadedSideBySideSettings = Partial<ITeeSheetSideBySideSettingsV1> & { version?: number };

function migrateSideBySideSettings(loadedSideBySideSettings: TLoadedSideBySideSettings): ITeeSheetSideBySideSettingsV1 {
  const loaded = loadedSideBySideSettings;
  return {
    version: 1,
    enabled: loaded.enabled ?? defaultSideBySideSettings.enabled,
    view: loaded.view ?? defaultSideBySideSettings.view,
    selectedDivisionIds: loaded.selectedDivisionIds ?? [],
    hideTurnTimes: loaded.hideTurnTimes ?? false,
    hideBlockedTimes: loaded.hideBlockedTimes ?? false,
    syncDivisionScroll: loaded.syncDivisionScroll ?? false,
    disableAutoScroll: loaded.disableAutoScroll ?? false,
    hideBookingCategories: loaded.hideBookingCategories ?? false,
  };
}

export function useTeeSheetSideBySideSettings(): IUseTeeSheetSideBySideSettings {
  const windowSize = useWindowSize();

  const [sideBySideSettings, setSideBySideSettings] = useState<ITeeSheetSideBySideSettings>(
    cloneDeep(defaultSideBySideSettings),
  );

  const sideBySideTeeSheetActive =
    sideBySideSettings != null && sideBySideSettings.enabled && sideBySideSettings.selectedDivisionIds?.length > 0;

  const teeSheetView: TTeeSheetView = useMemo(() => {
    if (!sideBySideTeeSheetActive) {
      return "default";
    } else {
      if (windowSize.width <= TABLET_WIDTH) {
        return "stacked";
      } else {
        return sideBySideSettings.view;
      }
    }
  }, [sideBySideTeeSheetActive, windowSize.width, sideBySideSettings.view]);

  function loadSideBySideSettings(sideBySideSettingsKeyPrefix: string) {
    if (!isLocalStorageAvailable || sideBySideSettingsKeyPrefix == null) {
      return;
    }

    const sideBySideSettingsKey = `${sideBySideSettingsKeyPrefix}_${SIDE_BY_SIDE_SETTINGS_KEY_SUFFIX}`;

    const rawSideBySideSettings = localStorage.getItem(sideBySideSettingsKey);

    if (!rawSideBySideSettings) {
      setSideBySideSettings(cloneDeep(defaultSideBySideSettings));
      return;
    }

    try {
      const loadedSideBySideSettings = JSON.parse(rawSideBySideSettings) as TLoadedSideBySideSettings;

      const migratedSideBySideSettings: ITeeSheetSideBySideSettingsV1 =
        migrateSideBySideSettings(loadedSideBySideSettings);

      if (JSON.stringify(loadedSideBySideSettings) !== JSON.stringify(migratedSideBySideSettings)) {
        localStorage.setItem(sideBySideSettingsKey, JSON.stringify(migratedSideBySideSettings));
      }

      setSideBySideSettings(migratedSideBySideSettings);
    } catch {
      console.warn("Invalid tee sheet side by side settings in storage");
    }
  }

  const updateSideBySideSettings: IUpdateTeeSheetSideBySideSettings = (
    sideBySideSettingsKeyPrefix,
    updatedSideBySideSettings,
  ) => {
    if (!isLocalStorageAvailable || sideBySideSettingsKeyPrefix == null || updatedSideBySideSettings == null) {
      return;
    }

    const sideBySideSettingsKey = `${sideBySideSettingsKeyPrefix}_${SIDE_BY_SIDE_SETTINGS_KEY_SUFFIX}`;

    const updatedSideBySideSettingsToSave: ITeeSheetSideBySideSettingsV1 = {
      ...migrateSideBySideSettings(updatedSideBySideSettings),
      version: CURRENT_SIDE_BY_SIDE_SETTINGS_VERSION,
    };

    setSideBySideSettings(updatedSideBySideSettingsToSave);
    localStorage.setItem(sideBySideSettingsKey, JSON.stringify(updatedSideBySideSettingsToSave));
  };

  return {
    sideBySideSettings,
    teeSheetView,
    loadSideBySideSettings,
    updateSideBySideSettings,
  };
}
